import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import type { MemberNotificationItem } from "@/features/public/api";

const typeIcons: Record<string, { icon: string; color: string; bg: string }> = {
  ADMIN_NEWS: { icon: "ri-newspaper-fill", color: "text-[#E8521A]", bg: "bg-orange-50" },
  ADMIN_EVENT: { icon: "ri-calendar-event-fill", color: "text-teal-600", bg: "bg-teal-50" },
  ADMIN_GALLERY: { icon: "ri-image-fill", color: "text-amber-500", bg: "bg-amber-50" },
  ADMIN_PRODUCT: { icon: "ri-shopping-bag-3-fill", color: "text-[#E8521A]", bg: "bg-orange-50" },
  ADMIN_LIVE: { icon: "ri-live-fill", color: "text-rose-500", bg: "bg-rose-50" },
  ADMIN_LEISURE: { icon: "ri-play-circle-fill", color: "text-emerald-600", bg: "bg-emerald-50" },
  CHAT_MESSAGE: { icon: "ri-chat-3-fill", color: "text-teal-600", bg: "bg-teal-50" },
  ORDER_STATUS: { icon: "ri-truck-fill", color: "text-sky-600", bg: "bg-sky-50" },
  default: { icon: "ri-notification-3-fill", color: "text-[#E8521A]", bg: "bg-orange-50" }
};

interface NotificationsPanelProps {
  notifications: MemberNotificationItem[];
  loading: boolean;
  memberLoggedIn: boolean;
  pushSupported: boolean;
  pushEnabled: boolean;
  pushRequesting: boolean;
  pushError?: string | null;
  onEnablePush: () => void;
  onDisablePush: () => void;
  onMarkAllRead: () => void;
  onOpenNotification: (notification: MemberNotificationItem) => void;
  onClose?: () => void;
}

function formatNotificationTime(value: string) {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const diffInMinutes = Math.round((parsedDate.getTime() - Date.now()) / 60000);
  const formatter = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

  if (Math.abs(diffInMinutes) < 60) {
    return formatter.format(diffInMinutes, "minute");
  }

  const diffInHours = Math.round(diffInMinutes / 60);
  if (Math.abs(diffInHours) < 24) {
    return formatter.format(diffInHours, "hour");
  }

  const diffInDays = Math.round(diffInHours / 24);
  return formatter.format(diffInDays, "day");
}

export default function NotificationsPanel({
  notifications,
  loading,
  memberLoggedIn,
  pushSupported,
  pushEnabled,
  pushRequesting,
  pushError,
  onEnablePush,
  onDisablePush,
  onMarkAllRead,
  onOpenNotification,
  onClose
}: NotificationsPanelProps) {
  const navigate = useNavigate();
  const unreadCount = useMemo(() => notifications.filter((notification) => !notification.read).length, [notifications]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-[#E8521A] text-white text-xs flex items-center justify-center font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={onMarkAllRead} className="text-xs text-[#E8521A] hover:underline cursor-pointer whitespace-nowrap">
              Tout lire
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>
      </div>

      {!memberLoggedIn ? (
        <div className="p-4">
          <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-5 text-center">
            <i className="ri-lock-2-line text-2xl text-gray-300"></i>
            <p className="mt-3 text-sm font-semibold text-gray-900">Connectez-vous pour recevoir les notifications</p>
            <p className="mt-1 text-xs text-gray-500">Les alertes push et les messages de vos squads apparaîtront ici.</p>
            <button
              onClick={() => {
                onClose?.();
                navigate("/formulaires?tab=login&redirect=%2Fsocial");
              }}
              className="mt-4 inline-flex items-center justify-center rounded-xl bg-[#E8521A] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#d44a16] cursor-pointer"
            >
              Se connecter
            </button>
          </div>
        </div>
      ) : (
        <>
          {!pushEnabled && (
            <div className="mx-4 mt-4 rounded-xl bg-orange-50 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-orange-100">
                  <i className="ri-notification-3-line text-[#E8521A]"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900">Activer les notifications push</p>
                  <p className="text-xs text-gray-500">
                    {pushSupported
                      ? "Recevez les alertes admin et les messages privés, même hors de l'application."
                      : "Ce navigateur ne prend pas en charge les notifications push."}
                  </p>
                </div>
                {pushSupported && (
                  <button
                    onClick={onEnablePush}
                    disabled={pushRequesting}
                    className="flex-shrink-0 whitespace-nowrap rounded-lg bg-[#E8521A] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:opacity-60 cursor-pointer"
                  >
                    {pushRequesting ? "En cours..." : "Activer"}
                  </button>
                )}
              </div>
              {pushError && (
                <p className="mt-2 text-xs font-medium text-red-600">{pushError}</p>
              )}
            </div>
          )}

          {pushEnabled && (
            <div className="mx-4 mt-4 flex items-center justify-between gap-3 rounded-xl bg-teal-50 p-3">
              <div className="flex items-center gap-2">
                <i className="ri-check-double-line text-teal-600"></i>
                <p className="text-xs font-medium text-teal-700">Notifications push activées</p>
              </div>
              <button
                onClick={onDisablePush}
                disabled={pushRequesting}
                className="flex-shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 cursor-pointer"
              >
                {pushRequesting ? "..." : "Désactiver"}
              </button>
            </div>
          )}

          <div className="mt-3 divide-y divide-gray-50">
            {loading ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Chargement des notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Aucune notification pour le moment.</div>
            ) : (
              notifications.map((notification) => {
                const style = typeIcons[notification.type] || typeIcons.default;

                return (
                  <button
                    key={notification.id}
                    onClick={() => onOpenNotification(notification)}
                    className={`w-full flex items-start gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer text-left ${!notification.read ? "bg-orange-50/40" : ""}`}
                  >
                    <div className="relative flex-shrink-0">
                      {notification.avatarUrl ? (
                        <img src={notification.avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover object-[center_24%]" />
                      ) : (
                        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${style.bg}`}>
                          <i className={`${style.icon} ${style.color} text-lg`}></i>
                        </div>
                      )}
                      {notification.avatarUrl && (
                        <div className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${style.bg}`}>
                          <i className={`${style.icon} ${style.color} text-xs`}></i>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-snug ${!notification.read ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                        <span className="block">{notification.title}</span>
                        <span className="mt-0.5 block font-normal">{notification.body}</span>
                      </p>
                      <p className="mt-0.5 text-xs text-gray-400">{formatNotificationTime(notification.createdAt)}</p>
                    </div>
                    {!notification.read && (
                      <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-[#E8521A]"></span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
